
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import ResponsiveTopNavigation from '../../components/navigation/ResponsiveTopNavigation';
import { base_url } from 'Backend/api';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FaCamera } from "react-icons/fa";
import Cropper from "react-easy-crop";
import Footer from 'components/Footer';
import AppSpinner from 'components/ui/AppSpinner';
import Lottie from "lottie-react";
import logoutAnimation from "../../../public/assets/lottie/LogOut.json";
import deleteAccountAnimation from "../../../public/assets/lottie/Delete account.json";
import { validatePhoneNumber } from "utils/validatePhoneNumber";
import { toggleNotificationPreference } from "utils/notificationSettings";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
}

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

const DeleteAccountConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-3 font-[cairo]">
      <div className="relative w-full max-w-[360px] rounded-[8px] bg-white p-6 pt-4 shadow-[0_12px_30px_rgba(0,0,0,0.18)] md:p-4 md:pt-9">
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-3.5 top-3.5 text-[#1F2937] transition hover:opacity-75"
          aria-label="إغلاق"
        >
          <Icon name="X" size={20} />
        </button>

        <div className="mx-auto mb-2.5 flex h-[106px] w-[106px] items-center justify-center rounded-full ">
          <Lottie
            animationData={deleteAccountAnimation}
            loop
            className="h-[120px] w-[120px]"
          />
        </div>

        <h3 className="font-[cairo] text-center text-[20px] font-bold leading-tight text-[#1F2937] md:text-[22px]">
          {title}
        </h3>
        <p className="mt-1 font-[cairo] mb-2 text-center text-[13px] font-medium leading-tight text-[#8B8B8B] md:text-[14px]">
          {description}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
         
          <button
            type="button"
            onClick={onCancel}
            className="h-[40px] rounded-[8px] border border-[#F93209] text-[14px] font-medium text-[#F93209] transition hover:bg-[#FFF3EF]"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="h-[40px] rounded-[8px] bg-[#F93209] text-[14px] font-medium text-white transition hover:bg-[#e82f05]"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const LogoutConfirmDialog = ({ open, onCancel, onConfirm }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-3 font-[cairo]">
      <div className="relative w-full max-w-[360px] rounded-[14px] bg-white p-6 pt-8 shadow-[0_12px_30px_rgba(0,0,0,0.18)] md:p-4 md:pt-9">
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-3.5 top-3.5 text-[#1F2937] transition hover:opacity-75"
          aria-label="إغلاق"
        >
          <Icon name="X" size={20} />
        </button>

        <div className="mx-auto mb-2.5 flex h-[106px] w-[106px] items-center justify-center rounded-full ">
          <Lottie
            animationData={logoutAnimation}
            loop
            className="h-[90px] w-[90px]"
          />
        </div>

        <h3 className="text-center text-[20px] font-[cairo] font-bold leading-tight text-[#1F2937] md:text-[22px]">
          تسجيل الخروج
        </h3>
        <p className="mt-1 font-[cairo] mb-2 text-center text-[13px] font-medium leading-tight text-[#8B8B8B] md:text-[14px]">
          هل أنت متأكد من تسجيل الخروج؟ سيتم الحفاظ على بياناتك
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-[40px] rounded-[8px] border border-[#F93209] text-[14px] font-medium text-[#F93209] transition hover:bg-[#FFF3EF]"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="h-[40px] rounded-[8px] bg-[#F93209] text-[14px] font-medium text-white transition hover:bg-[#e82f05]"
          >
            تسجيل الخروج
          </button>
        </div>
      </div>
    </div>
  );
};

const AccountPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsToggling, setNotificationsToggling] = useState(false);
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formDataState, setFormDataState] = useState({
    full_name: "",
    email: "",
    phone: "",
    profile_image: null,
  });
  const fileInputRef = useRef(null);
  const editPreviewUrlRef = useRef(null);

  const [editPreviewSrc, setEditPreviewSrc] = useState(null);
  const [cropSourceSrc, setCropSourceSrc] = useState(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [showCropModal, setShowCropModal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [ordersCount, setOrdersCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);


const openDeleteAccountModal = () => setConfirmDeleteAccount(true);
const closeDeleteAccountModal = () => setConfirmDeleteAccount(false);
const openLogoutModal = () => setConfirmLogout(true);
const closeLogoutModal = () => setConfirmLogout(false);


const handleDeleteAccount = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${base_url}/api/me/account`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();

    if (res.ok && data.status) {
      toast.success(data.message || 'تم حذف الحساب بنجاح', { duration: 1500 });
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setTimeout(() => navigate('/', { replace: true }), 1600);
    } else {
      toast.error(data.message || 'حدث خطأ أثناء حذف الحساب');
    }
  } catch (error) {
    console.error(error);
    toast.error('حدث خطأ في الاتصال بالسيرفر');
  } finally {
    setLoading(false);
    closeDeleteAccountModal();
  }
};




  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(`${base_url}/api/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (data.status) {
          setUser(data.data);
          setFormDataState({
            full_name: data.data.full_name ?? "",
            email: data.data.email ?? "",
            phone: data.data.phone ?? "",
            profile_image: null,
          });
          const enabled = !!data.data.notifications_enabled;
          setNotificationsEnabled(enabled);
          localStorage.setItem("notifications_enabled", enabled ? "1" : "0");
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const [ordersRes, favRes] = await Promise.all([
          fetch(`${base_url}/api/orders`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          }),
          fetch(`${base_url}/api/favorites`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          }),
        ]);
        const ordersJson = await ordersRes.json().catch(() => ({}));
        const favJson = await favRes.json().catch(() => ({}));
        if (cancelled) return;
        const list =
          ordersJson?.data?.orders ??
          ordersJson?.data ??
          (Array.isArray(ordersJson?.data) ? ordersJson.data : []);
        const orderLen = Array.isArray(list) ? list.length : 0;
        const favLen = Array.isArray(favJson?.data) ? favJson.data.length : 0;
        setOrdersCount(orderLen);
        setFavoritesCount(favLen);
      } catch {
        if (!cancelled) {
          setOrdersCount(0);
          setFavoritesCount(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleLogout = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${base_url}/api/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();

    localStorage.removeItem('token');
    localStorage.removeItem('user');

    if (res.ok && data.status) {
      toast.success(data.message || 'تم تسجيل الخروج بنجاح', { duration: 1500 });
      setTimeout(() => {
        toast.dismiss(); // يشيل كل التوستات
        navigate('/', { replace: true });
      }, 1600);
    } else {
      toast.error(data.message || 'حدث خطأ أثناء تسجيل الخروج');
    }
  } catch (error) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.error('حدث خطأ في الاتصال بالسيرفر');
    setTimeout(() => navigate('/', { replace: true }), 1000);
  } finally {
    setLoading(false);
    closeLogoutModal();
  }
};

  const revokeEditPreviewUrl = () => {
    if (editPreviewUrlRef.current) {
      URL.revokeObjectURL(editPreviewUrlRef.current);
      editPreviewUrlRef.current = null;
    }
  };

  const resetEditDraft = () => {
    revokeEditPreviewUrl();
    setEditPreviewSrc(null);
    setCropSourceSrc(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setShowCropModal(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (user) {
      setFormDataState({
        full_name: user.full_name ?? "",
        email: user.email ?? "",
        phone: user.phone ?? "",
        profile_image: null,
      });
    }
  };

  const openEditModal = () => {
    resetEditDraft();
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    resetEditDraft();
  };

  const handleSelectImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCroppedAreaPixels(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropSourceSrc(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropSourceSrc(null);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCropConfirm = async () => {
    if (!cropSourceSrc || !croppedAreaPixels) {
      toast.error("يرجى ضبط الصورة قبل الحفظ");
      return;
    }
    try {
      const blob = await getCroppedImg(cropSourceSrc, croppedAreaPixels);
      if (!blob) {
        toast.error("تعذر معالجة الصورة");
        return;
      }
      revokeEditPreviewUrl();
      const previewUrl = URL.createObjectURL(blob);
      editPreviewUrlRef.current = previewUrl;
      setEditPreviewSrc(previewUrl);
      setFormDataState((prev) => ({
        ...prev,
        profile_image: new File([blob], "profile.jpg", { type: "image/jpeg" }),
      }));
      setShowCropModal(false);
      setCropSourceSrc(null);
    } catch (error) {
      console.error(error);
      toast.error("تعذر قص الصورة");
    }
  };

  const supportItems = [
    {
      id: "help",
      label: "المساعدة والدعم",
      icon: "Headphones",
      iconColor: "#14B8A6",
      onClick: () => {
        window.open("https://wa.me/963996154965", "_blank");
      },
    },
    {
      id: "terms",
      label: "الشروط والأحكام",
      icon: "FileText",
      iconColor: "#6B7280",
      onClick: () => navigate("/terms-and-conditions"),
    },
    {
      id: "privacy",
      label: "سياسة الخصوصية",
      icon: "Shield",
      iconColor: "#EF4444",
      onClick: () => navigate("/privacy-policy"),
    },
    {
      id: "removeAccount",
      label: "حذف الحساب",
      icon: "Trash2",
      iconColor: "#EF4444",
      onClick: openDeleteAccountModal,
    },
  ];

  const menuDivider = "border-b border-[#EEEEEE]";

  const iconCircleClass =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6]";

  const handleToggleNotifications = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("يجب تسجيل الدخول أولاً");
      return;
    }

    const nextEnabled = !notificationsEnabled;
    setNotificationsToggling(true);

    try {
      const result = await toggleNotificationPreference(nextEnabled, token);

      if (result.ok) {
        const enabled = result.enabled ?? nextEnabled;
        setNotificationsEnabled(enabled);
        setUser((prev) =>
          prev ? { ...prev, notifications_enabled: enabled } : prev
        );
        toast.success(result.message || "تم تحديث الإشعارات");
      } else {
        toast.error(result.message || "فشل تحديث الإشعارات");
      }
    } catch (error) {
      console.error(error);
      toast.error("فشل تحديث الإشعارات");
    } finally {
      setNotificationsToggling(false);
    }
  };

  const handleUpdateProfile = async () => {
    const fullName = formDataState.full_name?.trim();
    const email = formDataState.email?.trim();
    const phone = formDataState.phone?.trim();

    if (!fullName) {
      toast.error("الاسم مطلوب");
      return;
    }
    if (!email) {
      toast.error("البريد الإلكتروني مطلوب");
      return;
    }
    const phoneError = validatePhoneNumber(phone);
    if (phoneError) {
      toast.error(phoneError);
      return;
    }

    setSavingProfile(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("full_name", fullName);
      formData.append("email", email);
      formData.append("phone", phone);

      if (formDataState.profile_image) {
        formData.append("profile_image", formDataState.profile_image);
      }

      const res = await fetch(`${base_url}/api/me/profile/update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.status) {
        toast.success(data.message || "تم تحديث البيانات");
        setUser(data.data);
        if (data.data) {
          localStorage.setItem("user", JSON.stringify(data.data));
        }
        setShowEditModal(false);
        revokeEditPreviewUrl();
        setEditPreviewSrc(null);
        setFormDataState({
          full_name: data.data.full_name ?? "",
          email: data.data.email ?? "",
          phone: data.data.phone ?? "",
          profile_image: null,
        });
      } else {
        const errMsg =
          data?.message ||
          (data?.errors && Object.values(data.errors).flat()[0]) ||
          "حدث خطأ أثناء التحديث";
        toast.error(errMsg);
      }
    } catch (error) {
      console.error(error);
      toast.error("فشل التحديث");
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => () => revokeEditPreviewUrl(), []);

  return (
    <>
      <Helmet>
                     <title>الملف الشخصى - يسلمو</title>
                 </Helmet>

      <Toaster
        position="top-right"
        toastOptions={{
          style: { fontFamily: 'Cairo, sans-serif', fontWeight: '500', fontSize: '14px', textAlign: 'right', direction: 'rtl' },
          success: { duration: 5000 }, error: { duration: 5000 }
        }}
      />

      <ResponsiveTopNavigation />
      {loadingUser && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <AppSpinner />
        </div>
      )}

      <main className="min-h-screen bg-[#F5F5F5] pb-24 font-[cairo]" dir="rtl">
        <div className="mx-auto mt-14 max-w-lg space-y-3 px-3 pb-8 pt-1 md:mt-16 md:px-4">
          {/* بطاقة الملف الشخصي */}
          <div className="relative flex items-center gap-3 rounded-[6px] mt-4  bg-white p-4 ">
            <div className="relative h-[62px] w-[62px] shrink-0 overflow-hidden rounded-full ring-1 ring-black/[0.06]">
              {user?.profile_image_url ? (
                <img
                  src={`${base_url}/${user.profile_image_url}`}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-[#F3F4F6]">
                  <Icon name="User" size={32} color="#9CA3AF" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-0.5 text-right">
              <h2 className="truncate font-[cairo] text-[14px] font-medium leading-snug text-[#111827]">
                {user?.full_name || "—"}
              </h2>
              <p className="truncate font-[cairo] text-[11px] leading-snug font-semibold">{user?.email || "—"}</p>
              <p className="truncate font-[cairo] text-[11px] leading-snug font-normal">{user?.phone || "—"}</p>
            </div>
            <button
              type="button"
              onClick={openEditModal}
              className="shrink-0 rounded-full p-2 text-[#9CA3AF] transition-colors hover:bg-[#F3F4F6]"
              aria-label="تعديل الملف الشخصي"
            >
              <Icon name="Pencil" size={14} color="currentColor" />
            </button>
          </div>

          {/* القائمة الرئيسية */}
          <div className="overflow-hidden rounded-[6px]  bg-white ">
            <button
              type="button"
              onClick={() => navigate("/orders")}
              className={`flex w-full items-center gap-3 px-4 py-3.5 text-right transition-colors hover:bg-[#FAFAFA] ${menuDivider}`}
            >
              <div className={iconCircleClass}>
                <Icon name="ShoppingBag" size={20} color="#F93209" />
              </div>
              <span className="flex-1 text-[13px] font-medium text-[#111827]">طلباتي</span>
              <div className="flex shrink-0 items-center gap-2">
                {ordersCount > 0 && (
                  <span className="flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-[#F93209] px-1.5 text-[10px] font-medium leading-none text-white">
                    {ordersCount > 99 ? "99+" : ordersCount}
                  </span>
                )}
                <Icon name="ChevronLeft" size={20} color="#9CA3AF" />
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate("/my-addresses")}
              className={`flex w-full items-center gap-3 px-4 py-3.5 text-right transition-colors hover:bg-[#FAFAFA] ${menuDivider}`}
            >
              <div className={iconCircleClass}>
                <Icon name="MapPin" size={20} color="#2563EB" />
              </div>
              <span className="flex-1 text-[13px] font-medium text-[#111827]">العناوين</span>
              <Icon name="ChevronLeft" size={20} color="#9CA3AF" className="shrink-0" />
            </button>

            <button
              type="button"
              onClick={() => navigate("/favorites")}
              className={`flex w-full items-center gap-3 px-4 py-3.5 text-right transition-colors hover:bg-[#FAFAFA] ${menuDivider}`}
            >
              <div className={iconCircleClass}>
                <Icon name="Heart" size={20} color="#EF4444" />
              </div>
              <span className="flex-1 text-[13px] font-medium text-[#111827]">المفضلة</span>
              <div className="flex shrink-0 items-center gap-2">
                {favoritesCount > 0 && (
                  <span className="flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-[#F93209] px-1.5 text-[10px] font-medium leading-none text-white">
                    {favoritesCount > 99 ? "99+" : favoritesCount}
                  </span>
                )}
                <Icon name="ChevronLeft" size={20} color="#9CA3AF" />
              </div>
            </button>

            <div className="flex w-full items-center gap-3 px-4 py-3.5 text-right">
              <div className={iconCircleClass}>
                <Icon name="Settings" size={20} color="#6B7280" />
              </div>
              <span className="flex-1 text-[13px] font-medium text-[#111827]">تفعيل الإشعارات</span>
              <div
                dir="ltr"
                className="shrink-0"
                onClick={(e) => e.stopPropagation()}
                role="presentation"
              >
                <button
                  type="button"
                  onClick={handleToggleNotifications}
                  disabled={notificationsToggling}
                  className={`relative h-[26px] w-11 shrink-0 rounded-full transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-60 ${
                    notificationsEnabled ? "bg-[#F93209]" : "bg-[#D1D5DB]"
                  }`}
                  aria-pressed={notificationsEnabled}
                  aria-busy={notificationsToggling}
                  aria-label="تفعيل الإشعارات"
                >
                  <span
                    className={`absolute top-1 h-[18px] w-[18px] rounded-full bg-white shadow-md transition-[left] duration-300 ${
                      notificationsEnabled ? "left-[22px]" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* الدعم والمساعدة */}
          <div className="overflow-hidden rounded-[6px]  bg-white ">
            <div className="border-b border-[#EEEEEE] px-4 pb-2 pt-3">
              <h3 className="text-right text-[15px] font-semibold font-[cairo] text-[#111827]">الدعم والمساعدة</h3>
            </div>
            {supportItems.map((item, index) => (
              <div key={item.id}>
                <button
                  type="button"
                  onClick={item.onClick}
                  className={`flex w-full items-center gap-3 px-4 py-3.5 text-right transition-colors hover:bg-[#FAFAFA] ${
                    index < supportItems.length - 1 ? menuDivider : ""
                  }`}
                >
                  <div className={iconCircleClass}>
                    <Icon name={item.icon} size={20} color={item.iconColor} />
                  </div>
                  <span className="flex-1 text-[13px] font-medium text-[#111827]">{item.label}</span>
                  <Icon name="ChevronLeft" size={20} color="#9CA3AF" className="shrink-0" />
                </button>
              </div>
            ))}
          </div>

          {/* تسجيل الخروج */}
          <button
            type="button"
            onClick={openLogoutModal}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-[6px]  py-3.5  transition-colors bg-[#FFEBEB] disabled:cursor-not-allowed disabled:opacity-70"
          >
            <Icon name="LogOut" size={16} color="#F93209" />
            <span className="text-[13px] font-semibold text-[#F93209]">
              {loading ? "جارٍ تسجيل الخروج..." : "تسجيل الخروج"}
            </span>
          </button>

          <p className="text-center text-[12px] text-[#9CA3AF]">الإصدار 1.2.1</p>
        </div>
        {showEditModal && (
          <div
            dir="rtl"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => !savingProfile && closeEditModal()}
          >
            <div
              className="w-[90%] max-w-md space-y-4 rounded-[8px] bg-white p-6"
              onClick={(e) => e.stopPropagation()}
            >

              <h2 className="text-lg font-medium font-[cairo] text-center ">تعديل الملف الشخصي</h2>



              {/* Image */}
              <div>
                <label className="block text-sm font-medium mb-1 text-right">الصورة الشخصية</label>
                <div className="flex justify-center mb-4">

                  <div
                    className="relative w-24 h-24 group cursor-pointer"
                    onClick={() => fileInputRef.current.click()}
                  >

                    <img
                      src={
                        editPreviewSrc
                          ? editPreviewSrc
                          : user?.profile_image_url
                            ? `${base_url}/${user.profile_image_url}`
                            : "/assets/images/default-avatar.png"
                      }
                      alt=""
                      className="w-full h-full rounded-full object-cover"
                    />

                    <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                      <FaCamera className="text-white text-xl" />
                    </div>

                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleSelectImage}
                  />

                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1 text-right">الاسم</label>
                <input
                  type="text"
                  value={formDataState.full_name}
                  onChange={(e) =>
                    setFormDataState({ ...formDataState, full_name: e.target.value })
                  }
                  className="w-full border rounded-[8px] p-2"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1 text-right">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={formDataState.email}
                  onChange={(e) =>
                    setFormDataState({ ...formDataState, email: e.target.value })
                  }
                  className="w-full border rounded-[8px] p-2"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium mb-1 text-right">رقم الهاتف</label>
                <input
                  type="text"
                  value={formDataState.phone}
                  onChange={(e) =>
                    setFormDataState({ ...formDataState, phone: e.target.value })
                  }
                  className="w-full border rounded-[8px] p-2"
                />
              </div>


              

              <div className="flex gap-2">
              <button
                  type="button"
                  disabled={savingProfile}
                  onClick={handleUpdateProfile}
                  className="flex-1 rounded-[8px] bg-primary py-2 text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savingProfile ? "جاري الحفظ..." : "حفظ التعديلات"}
                </button>
                <button
                  type="button"
                  disabled={savingProfile}
                  onClick={closeEditModal}
                  className="flex-1 rounded-[8px] bg-gray-200 py-2 disabled:opacity-60"
                >
                  إلغاء
                </button>

                
              </div>

            </div>
          </div>
        )}

        <LogoutConfirmDialog
          open={confirmLogout}
          onCancel={closeLogoutModal}
          onConfirm={handleLogout}
        />

        <DeleteAccountConfirmDialog
          open={confirmDeleteAccount}
          title="حذف الحساب"
          description="هل أنت متأكد من حذف الحساب نهائياً؟ لا يمكن التراجع عن هذا الإجراء."
          confirmLabel="حذف الحساب"
          onCancel={closeDeleteAccountModal}
          onConfirm={handleDeleteAccount}
        />

        {showCropModal && cropSourceSrc && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">

            <div className="bg-white p-4 rounded-xl w-[400px]">

              <div className="relative w-full h-[300px]">

                <Cropper
                  image={cropSourceSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                />

              </div>

              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full mt-4"
              />

              <div className="flex justify-end gap-2 mt-4">

                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="rounded bg-gray-200 px-4 py-1"
                >
                  إلغاء
                </button>

                <button
                  type="button"
                  onClick={handleCropConfirm}
                  className="rounded bg-primary px-4 py-1 text-white"
                >
                  حفظ
                </button>

              </div>

            </div>

          </div>
        )}
      </main>
      {/* <Footer/> */}
    </>
  );
};

export default AccountPage;